const DEFAULT_TTL_SECONDS = 21600; // 6 hours

export type CacheItem = {
  expiresAt: number;
  value: string;
};

export interface LocalCache<CacheKey> {
  get: (key: CacheKey) => string | undefined;
  set: (
    key: CacheKey,
    value: string,
    ttl?: number
  ) => { key: CacheKey; value: string };
}

interface LocalCacheOptions<CacheKey> {
  prefix: string;
  defaultTtlSeconds?: number;
  maxItems?: number;
  cacheKey: (key: CacheKey) => string;
}

/**
 * Creates a `LocalCache` with the given options. A `LocalCache` is a simple
 * key-value store that uses localStorage to store items with an expiration
 * time, and automatically removes expired items on `get` and `set` operations.
 * Additionally, it limits the number of items in the cache to a maximum of
 * `maxItems`. When setting a new item, if the cache exceeds the maximum number
 * of items, it removes the items that are closest to expiration.
 *
 * The cache is prefixed with the given `prefix` to avoid collisions with other
 * caches.
 *
 * @param options The options for the cache.
 * @returns A local cache instance.
 */
export const localCache: {
  (options: Omit<LocalCacheOptions<string>, "cacheKey">): LocalCache<string>;
  <CacheKey>(options: LocalCacheOptions<CacheKey>): LocalCache<CacheKey>;
} = <CacheKey = string>({
  prefix,
  defaultTtlSeconds = DEFAULT_TTL_SECONDS,
  maxItems = 1000,
  cacheKey = (key) => String(key),
}: LocalCacheOptions<CacheKey>): LocalCache<CacheKey> => {
  const constructKey = (key: CacheKey) => {
    return `${prefix}:${cacheKey(key)}`;
  };
  const keyPrefix = `${prefix}:`;

  const getCacheItems = () =>
    Object.keys(localStorage).filter((key) => key.startsWith(keyPrefix));

  /**
   * Iterates through all items in localStorage with this cache's prefix and
   * removes expired or invalid items.
   */
  const removeExpiredItemsFromCache = () => {
    const cacheItems = getCacheItems();
    for (const key of cacheItems) {
      const localStorageItem = localStorage.getItem(key);
      if (localStorageItem == null) {
        continue;
      }

      try {
        const parsedItem = JSON.parse(localStorageItem);
        if (
          parsedItem == null ||
          // Basic schema validation
          typeof parsedItem !== "object" ||
          !("expiresAt" in parsedItem) ||
          typeof parsedItem.expiresAt !== "number" ||
          typeof parsedItem.value !== "string" ||
          // Check if the item has expired
          parsedItem.expiresAt < Date.now()
        ) {
          localStorage.removeItem(key);
        }
      } catch {
        // Error while parsing the item, remove it
        localStorage.removeItem(key);
      }
    }
  };

  /**
   * Removes the oldest items from the cache if the number of items exceeds
   * the given maximum number of items.
   *
   * @param max The maximum number of items to keep in the cache
   */
  const cullCache = (max = maxItems) => {
    const cacheItems = getCacheItems();

    // If the cache exceeds the maximum number of items, remove the oldest items
    if (cacheItems.length > max) {
      // Parse each item and sort them by expiration time
      const sortedItems = cacheItems
        .map((key) => {
          const item = localStorage.getItem(key);
          const parsedItem = item != null ? JSON.parse(item) : null;
          return {
            key,
            value: parsedItem as CacheItem | null,
          };
        })
        .sort((a, b) => {
          const aTime = a.value?.expiresAt;
          const bTime = b.value?.expiresAt;
          if (aTime != null && bTime != null) {
            return aTime - bTime;
          } else if (aTime != null) {
            return 1;
          } else if (bTime != null) {
            return -1;
          }
          return 0;
        });

      // Remove the oldest items until the cache is within the limit
      const itemsToRemove = sortedItems
        .slice(0, cacheItems.length - max)
        .map((item) => item.key);
      for (const item of itemsToRemove) {
        localStorage.removeItem(item);
      }
    }
  };

  const get = (key: CacheKey) => {
    removeExpiredItemsFromCache();

    const localStorageItem = localStorage.getItem(constructKey(key));
    if (localStorageItem == null) {
      return undefined;
    }

    // `removeExpiredItemsFromCache` will have removed expired and invalid
    // items from localStorage, so we should be able to safely assume that all
    // items in the cache are valid.
    const parsedItem: CacheItem = JSON.parse(localStorageItem);
    return parsedItem.value;
  };

  const set = (
    key: CacheKey,
    value: string,
    ttlSeconds: number = defaultTtlSeconds
  ) => {
    removeExpiredItemsFromCache();

    const expiresAt = Date.now() + ttlSeconds * 1000;
    const cacheItem: CacheItem = { expiresAt, value };

    localStorage.setItem(constructKey(key), JSON.stringify(cacheItem));
    cullCache(maxItems);
    return { key, value };
  };

  return { get, set };
};
